import { ROLES } from "@infinitaslearning/module-he-common";
import axios, { type AxiosError } from "axios";
import { ERRORS } from "constants/error-constants";
import type { Asset } from "models/asset/Asset";
import type { Book } from "models/book/Book";
import type { ApiRequestError } from "models/error/Error";
import cookieStorage from "persistence";
import { getAccountRole } from "persistence/access";
import { generatePathRoute } from "utils/pathBuilder";
import { CustomError, handlingResponse, logError } from "../utils";

const getAllBooks = async (): Promise<Book[]> => {
  try {
    const role = getAccountRole();
    if (role === ROLES.TEACHER) {
      const favoriteBooks = await axios.get(generatePathRoute("/api/additionals/favoriteBooks"), {
        headers: { Authorization: cookieStorage.getToken() },
      });
      return handlingResponse<Book[]>([200], "Error getting favorite books")(favoriteBooks);
    }
    const licenses = await axios.get(generatePathRoute("/api/idp/licenses"), {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<Book[]>([200], "Error getting licences")(licenses);
  } catch (error) {
    const { message, status } = logError(error as AxiosError<ApiRequestError>);
    throw new CustomError({ message, status, type: ERRORS.GET_ALL_BOOKS_FAILED });
  }
};

const getOneBook = async (bookId: string): Promise<Book> => {
  try {
    const result = await axios.get(generatePathRoute(`/api/additionals/book/${bookId}`), {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<Book>([200], `Error getting book with bookId ${bookId}`)(result);
  } catch (error) {
    const { message, status } = logError(error as AxiosError<ApiRequestError>);
    throw new CustomError({ message, status, type: ERRORS.GET_BOOK_FAILED });
  }
};

const getOneBookAssets = async (bookId: string): Promise<Asset[]> => {
  try {
    const result = await axios.get(generatePathRoute(`/api/additionals/book/${bookId}/assets`), {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<Asset[]>(
      [200],
      `Error getting book assets with bookId ${bookId}`,
    )(result);
  } catch (error) {
    const { message, status } = logError(error as AxiosError<ApiRequestError>);
    throw new CustomError({ message, status, type: ERRORS.GET_ASSET_FAILED });
  }
};

const fetchBookTitle = async (bookId: string): Promise<{ id: string; title: string }> => {
  try {
    const result = await axios.get(generatePathRoute(`/api/additionals/book/${bookId}/title`), {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<{ id: string; title: string }>(
      [200],
      `Error getting book title with bookId ${bookId}`,
    )(result);
  } catch (error) {
    const { message, status } = logError(error as AxiosError<ApiRequestError>);
    throw new CustomError({ message, status, type: ERRORS.GET_TITLE_FAILED });
  }
};

export { getAllBooks, getOneBook, getOneBookAssets, fetchBookTitle };
